import { LitElement, css, html, nothing, render } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import {
  ASSIST_DRAFT_TEMPLATES,
  defaultFieldValues,
  mergeAssistDraftIntoComposer,
  renderAssistDraftTemplate,
  type AssistDraftTemplate,
} from "../chat/assist-draft-templates.ts";
import { icons } from "../icons.ts";

type PanelStep = "closed" | "pick" | "form";

@customElement("fleet-assist-draft")
export class FleetAssistDraft extends LitElement {
  @property({ type: Boolean }) disabled = false;

  /** Called with the full composer text after merge. */
  @property({ attribute: false }) onInsert?: (draft: string) => void;

  /** Current composer draft (for append merge). */
  @property({ type: String }) composerDraft = "";

  /**
   * Optional light-DOM host (composer grow rail). When set, the panel renders
   * there in-flow so the dock expands upward instead of overlaying the textarea.
   * When unset, the component looks for `[data-composer-grow-draft]` in the dock.
   */
  @property({ attribute: false }) panelHost: HTMLElement | null = null;

  /** Fires when the panel opens or closes (for closing sibling dock panels). */
  @property({ attribute: false }) onOpenChange?: (open: boolean) => void;

  @state() private step: PanelStep = "closed";
  @state() private selected: AssistDraftTemplate | null = null;
  @state() private fieldValues: Record<string, string> = {};
  @state() private formError: string | null = null;

  static styles = css`
    :host {
      position: relative;
      display: inline-flex;
    }
    .trigger {
      display: inline-flex;
      align-items: center;
      gap: 7px;
      height: 34px;
      padding: 0 11px;
      border-radius: 8px;
      border: 0;
      background: transparent;
      color: var(--fleet-assist-fg, rgba(210, 224, 240, 0.82));
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.01em;
      white-space: nowrap;
      cursor: pointer;
    }
    .trigger:hover:not(:disabled) {
      background: var(--fleet-assist-hover-bg, rgba(0, 170, 255, 0.08));
      color: var(--fleet-assist-hover-fg, rgba(255, 255, 255, 0.95));
    }
    .trigger:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
    .trigger svg {
      width: 16px;
      height: 16px;
    }
    .panel {
      position: absolute;
      left: var(--fleet-assist-panel-left, 0);
      right: var(--fleet-assist-panel-right, auto);
      top: var(--fleet-assist-panel-top, auto);
      bottom: var(--fleet-assist-panel-bottom, calc(100% + 8px));
      z-index: 40;
      width: var(--fleet-assist-panel-width, min(420px, 92vw));
      max-height: var(--fleet-assist-panel-max-height, min(70vh, 520px));
      overflow: auto;
      border-radius: 12px;
      border: 1px solid var(--border, #333);
      background: var(--bg, #111);
      box-shadow: 0 12px 40px rgba(0, 0, 0, 0.45);
      padding: 12px;
      box-sizing: border-box;
    }
    .panel__title {
      margin: 0 0 8px;
      font-size: 13px;
      font-weight: 700;
    }
    .panel__hint {
      margin: 0 0 10px;
      font-size: 11px;
      color: var(--muted, #888);
      line-height: 1.4;
    }
    .template {
      display: block;
      width: 100%;
      text-align: left;
      padding: 10px 12px;
      margin-bottom: 6px;
      border-radius: 8px;
      border: 1px solid var(--border, #333);
      background: transparent;
      color: inherit;
      cursor: pointer;
    }
    .template:hover {
      border-color: var(--accent, #6ea8fe);
      background: rgba(110, 168, 254, 0.08);
    }
    .template__label {
      display: block;
      font-size: 13px;
      font-weight: 600;
    }
    .template__desc {
      display: block;
      margin-top: 4px;
      font-size: 11px;
      color: var(--muted, #888);
    }
    .field {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 10px;
    }
    .field label {
      font-size: 11px;
      font-weight: 600;
      color: var(--muted, #aaa);
    }
    .field input {
      padding: 8px 10px;
      border-radius: 6px;
      border: 1px solid var(--border, #333);
      background: var(--bg-elevated, #1a1a1a);
      color: inherit;
      font-size: 12px;
    }
    .field input:focus {
      outline: none;
      border-color: var(--accent, #6ea8fe);
    }
    .actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
      margin-top: 12px;
    }
    .btn {
      padding: 8px 12px;
      border-radius: 8px;
      border: 1px solid var(--border, #333);
      background: transparent;
      color: inherit;
      font-size: 12px;
      cursor: pointer;
    }
    .btn--primary {
      border-color: var(--accent, #6ea8fe);
      background: rgba(110, 168, 254, 0.15);
      color: var(--accent, #6ea8fe);
      font-weight: 600;
    }
    .error {
      margin: 0 0 8px;
      font-size: 11px;
      color: #f87171;
    }
  `;

  private setStep(next: PanelStep) {
    const wasOpen = this.step !== "closed";
    const willOpen = next !== "closed";
    this.step = next;
    if (wasOpen !== willOpen) {
      this.onOpenChange?.(willOpen);
      this.toggleAttribute("open", willOpen);
    }
  }

  /** Close the draft panel (used by sibling dock controls). */
  close() {
    this.closePanel();
  }

  /**
   * JAISIU-3588 — open the picker from an external trigger (e.g. the
   * composer `+` popover). No-op if disabled or already open. The
   * sibling controls are closed by the host before invoking this so
   * the panel doesn't stack on top of Palace / Controls.
   */
  open() {
    if (this.disabled) {
      return;
    }
    this.openPicker();
  }

  private closePanel() {
    this.setStep("closed");
    this.selected = null;
    this.fieldValues = {};
    this.formError = null;
  }

  private openPicker() {
    if (this.disabled) {
      return;
    }
    if (this.step === "pick") {
      this.closePanel();
      return;
    }
    this.selected = null;
    this.formError = null;
    this.setStep("pick");
  }

  private pickTemplate(template: AssistDraftTemplate) {
    if (template.fields.length === 0) {
      this.insertTemplate(template.id, {});
      return;
    }
    this.selected = template;
    this.fieldValues = defaultFieldValues(template);
    this.setStep("form");
    this.formError = null;
  }

  private insertTemplate(templateId: string, values: Record<string, string>) {
    try {
      const body = renderAssistDraftTemplate(templateId, values);
      const merged = mergeAssistDraftIntoComposer(this.composerDraft, body);
      this.onInsert?.(merged);
      this.closePanel();
    } catch (err) {
      this.formError = err instanceof Error ? err.message : "Could not render template";
    }
  }

  private onFieldInput(id: string, value: string) {
    this.fieldValues = { ...this.fieldValues, [id]: value };
  }

  private renderPanel() {
    if (this.step === "closed") {
      return nothing;
    }
    const host = this.resolveHost();
    const panelClass = host ? "fleet-assist-draft-panel chat-compose__grow-panel" : "panel";
    return html`
      <div class=${panelClass} role="dialog" aria-label="AI Assist Draft templates">
        ${
          this.step === "pick"
            ? html`
                <p class="panel__title">Choose a template</p>
                <p class="panel__hint">
                  Templates append to your current draft. Fill in the fields for Semantic Lift before
                  inserting.
                </p>
                ${ASSIST_DRAFT_TEMPLATES.map(
                  (template) => html`
                    <button type="button" class="template" @click=${() => this.pickTemplate(template)}>
                      <span class="template__label">${template.label}</span>
                      <span class="template__desc">${template.description}</span>
                    </button>
                  `,
                )}
                <div class="actions">
                  <button type="button" class="btn" @click=${() => this.closePanel()}>Cancel</button>
                </div>
              `
            : this.selected
              ? html`
                  <p class="panel__title">${this.selected.label}</p>
                  <p class="panel__hint">${this.selected.description}</p>
                  ${this.formError ? html`<p class="error">${this.formError}</p>` : nothing}
                  ${this.selected.fields.map(
                    (field) => html`
                      <div class="field">
                        <label for=${`assist-${field.id}`}>${field.label}</label>
                        <input
                          id=${`assist-${field.id}`}
                          type="text"
                          .value=${this.fieldValues[field.id] ?? ""}
                          placeholder=${field.placeholder ?? ""}
                          @input=${(e: Event) =>
                            this.onFieldInput(field.id, (e.target as HTMLInputElement).value)}
                        />
                      </div>
                    `,
                  )}
                  <div class="actions">
                    <button type="button" class="btn" @click=${() => this.setStep("pick")}>Back</button>
                    <button
                      type="button"
                      class="btn btn--primary"
                      @click=${() => {
                        if (this.selected) {
                          this.insertTemplate(this.selected.id, this.fieldValues);
                        }
                      }}
                    >
                      Insert
                    </button>
                  </div>
                `
              : nothing
        }
      </div>
    `;
  }

  private resolveHost(): HTMLElement | null {
    if (this.panelHost) {
      return this.panelHost;
    }
    const dock = this.closest("[data-testid='chat-composer-dock']");
    const slot = dock?.querySelector("[data-composer-grow-draft]");
    return slot instanceof HTMLElement ? slot : null;
  }

  private syncPanelHost() {
    const host = this.resolveHost();
    if (!host) {
      return;
    }
    render(this.renderPanel(), host);
  }

  private clearPanelHost() {
    const host = this.resolveHost();
    if (host) {
      render(nothing, host);
    }
  }

  protected updated(): void {
    this.syncPanelHost();
  }

  disconnectedCallback(): void {
    this.clearPanelHost();
    super.disconnectedCallback();
  }

  render() {
    const host = this.resolveHost();
    return html`
      <button
        type="button"
        class="trigger"
        title="Insert a structured AI-assist prompt template into the composer."
        aria-label="Insert draft template"
        ?disabled=${this.disabled}
        @click=${() => this.openPicker()}
      >
        ${icons.bot}
        Draft
      </button>
      ${host ? nothing : this.renderPanel()}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "fleet-assist-draft": FleetAssistDraft;
  }
}
